import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostBinding,
    inject,
    Input,
    OnInit,
    Output,
    ViewEncapsulation
} from '@angular/core';
import { NgClass } from '@angular/common';

import { ApContentService, ApContentModule } from '@ap/content';

import content from './paginator.content.json';
import { ApAriaLabelPipe } from './paginator.pipe';

const PAGE_BUFFER_LEFT = 5;
const PAGE_BUFFER_RIGHT = 4;
const ONE_PAGE_UP = 1;
const ONE_PAGE_DOWN = -1;
const ZERO_TO_ONE_BASED = 1;
const SPLICE_AMOUNT = 4;
const DEFAULT_PAGE_SIZE = 50;

/**
 * Change event object that is emitted when the user selects a
 * different page size or navigates to another page.
 */
export class PageEvent {
    /** The current page index. */
    pageIndex: number;

    /**
     * Index of the page that was selected previously.
     *
     * @breaking-change 7.0.0 To be made into a required property.
     */
    previousPageIndex?: number;

    /** The current page size */
    pageSize: number;

    /** The current total number of items being paged */
    length: number;
}

// Boilerplate for applying mixins to MatPaginator.
/** @docs-private */
export class ApPaginatorBase {}

/**
 * @ngdoc component
 * @name ApPaginatorComponent
 * @module ApPaginatorModule
 * @description
 * Component to provide navigation between paged information. Displays the size of the current
 * page, user-selectable options to change that size, what items are being shown, and
 * navigational button to go to the previous or next page.
 * Paginator is not shown if the length of the items is smaller than the pagesize
 * @param {number=} pageIndex   page number current state.
 * @param {number=} pageSize   number of items displayed.
 * @param {length=} length     the total number of items in list.
 */
@Component({
    selector: 'ap-paginator',
    exportAs: 'apPaginator',
    templateUrl: 'paginator.component.html',
    styleUrls: ['./paginator.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [NgClass, ApContentModule, ApAriaLabelPipe]
})
export class ApPaginatorComponent implements OnInit {
    private readonly contentService = inject(ApContentService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    /**
     * The zero-based page index of the displayed list of items. Defaulted to 0.
     */
    @Input()
    get pageIndex(): number {
        return this._pageIndex;
    }

    set pageIndex(value: number) {
        this._pageIndex = Math.max(coerceNumberProperty(value), 0);
        this.changeDetectorRef.markForCheck();
    }

    /**
     * The length of the total number of items that are being paginated. Defaulted to 0.
     */
    @Input()
    get length(): number {
        return this._length;
    }

    set length(value: number) {
        this._length = coerceNumberProperty(value);

        // recalculate page range display when length changes after init
        if (this._initialized) {
            this.handlePageMove();
        }

        this.changeDetectorRef.markForCheck();
    }

    /**
     * Number of items to display on a page. By default set to 50.
     */
    @Input()
    get pageSize(): number {
        return this._pageSize;
    }

    set pageSize(value: number) {
        this._pageSize = Math.max(coerceNumberProperty(value), 0);
        this._updateDisplayedPageSizeOptions();
    }

    get currentPage(): number {
        return this.pageIndex + ZERO_TO_ONE_BASED;
    }

    /**
     * The set of provided page size options to display to the user.
     */
    @Input()
    get pageSizeOptions(): number[] {
        return this._pageSizeOptions;
    }

    set pageSizeOptions(value: number[]) {
        this._pageSizeOptions = (value || []).map((p) => coerceNumberProperty(p));
        this._updateDisplayedPageSizeOptions();
    }

    /**
     * Whether to hide the page size selection UI from the user.
     * Defaults to false
     */
    @Input()
    get hidePageSize(): boolean {
        return this._hidePageSize;
    }

    set hidePageSize(value: boolean) {
        this._hidePageSize = coerceBooleanProperty(value);
    }

    /**
     * Whether to show the first/last buttons UI to the user.
     * Defaults to false
     */
    @Input()
    get showFirstLastButtons(): boolean {
        return this._showFirstLastButtons;
    }

    set showFirstLastButtons(value: boolean) {
        this._showFirstLastButtons = coerceBooleanProperty(value);
    }

    /**
     * Aria label of paginator
     */
    @Input()
    public ariaLabel: string;

    @HostBinding('class.ap-paginator')
    protected apPaginator = true;

    /**
     * Event emitted when the paginator changes the page size or page index.
     */
    @Output()
    readonly page: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();

    protected ariaCurrent: string;

    /**
     * Displayed set of page size options. Will be sorted and include current page size.
     */
    protected _displayedPageSizeOptions: number[];

    protected pagesLeft: number[] = [];
    protected pagesRight: number[] = [];
    protected pages: number[] = [];

    private _initialized: boolean;

    private _pageIndex = 0;

    private _length = 0;

    private _pageSize: number;

    private _pageSizeOptions: number[] = [];

    private _hidePageSize = false;

    private _showFirstLastButtons = false;

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        this.ariaCurrent = this.contentService.get('paginator.aria.current.default');
        this._initialized = true;
        this.handlePageMove();
        this._updateDisplayedPageSizeOptions();
    }

    /** Advances to the next page if it exists. */
    protected nextPage(): void {
        if (!this.hasNextPage()) {
            return;
        }

        const previousPageIndex = this.pageIndex;
        this.pageIndex++;
        this._emitPageEvent(previousPageIndex);
    }

    /** Move back to the previous page if it exists. */
    protected previousPage(): void {
        if (!this.hasPreviousPage()) {
            return;
        }

        const previousPageIndex = this.pageIndex;
        this.pageIndex--;
        this._emitPageEvent(previousPageIndex);
    }

    /** Move to the first page if not already there. */
    protected firstPage(): void {
        // hasPreviousPage being false implies at the start
        if (!this.hasPreviousPage()) {
            return;
        }

        const previousPageIndex = this.pageIndex;
        this.pageIndex = 0;
        this._emitPageEvent(previousPageIndex);
    }

    /** Move to the last page if not already there. */
    protected lastPage(): void {
        // hasNextPage being false implies at the end
        if (!this.hasNextPage()) {
            return;
        }

        const previousPageIndex = this.pageIndex;
        this.pageIndex = this.getNumberOfPages() - 1;
        this._emitPageEvent(previousPageIndex);
    }

    protected goToPage(pageNumber: number): void {
        if (pageNumber > this.getLastPageNumber() || pageNumber === this.currentPage) {
            return;
        }
        const previousPageIndex = this.pageIndex;
        this.pageIndex = pageNumber - 1;
        this._emitPageEvent(previousPageIndex);
    }

    /** Whether there is a previous page. */
    protected hasPreviousPage(): boolean {
        return this.pageIndex >= 1 && this.pageSize !== 0;
    }

    /** Whether there is a next page. */
    protected hasNextPage(): boolean {
        const numberOfPages = this.getNumberOfPages();
        return this.pageIndex + ZERO_TO_ONE_BASED < numberOfPages && this.pageSize !== 0;
    }

    /** Calculate the number of pages */
    protected getNumberOfPages(): number {
        if (!this.pageSize) {
            return 0;
        }
        return Math.ceil(this.length / this.pageSize);
    }

    protected getRangeLabelRange(): string {
        const startIndex = this.pageIndex * this.pageSize;
        // If the start index exceeds the list length, do not try and fix the end index to the end.
        const endIndex = startIndex < this._length ? Math.min(startIndex + this.pageSize, this._length) : startIndex + this.pageSize;
        return `${(startIndex + 1).toString()}-${endIndex.toString()}`;
    }

    protected getRangeLabelTotal(): string {
        return Math.max(this._length, 0).toString();
    }

    protected getRangeLabel(): string {
        return [
            this.getRangeLabelRange(),
            this.contentService.get('paginator.range.of'),
            this.getRangeLabelTotal(),
            this.contentService.get('paginator.range.results')
        ].join(' ');
    }

    protected getLastPageNumber(): number {
        return Math.ceil(this._length / this.pageSize);
    }

    protected pageNotOnScreen(pageNumber: number): boolean {
        return this.pages.indexOf(pageNumber) === -1 && this.currentPage !== pageNumber && pageNumber !== 0;
    }

    protected handlePageMove(): void {
        const pages = Array.from(new Array(Math.ceil(this._length / this.pageSize)), (val, index) => index + ZERO_TO_ONE_BASED);
        const pagesReversed = [...pages].reverse();

        if (this.pageIndex >= pages.length) {
            this.pageIndex = pages.length - 1;
        }

        if (this.currentPage > this.getLastPageNumber() - PAGE_BUFFER_RIGHT) {
            this.pagesRight = pages.splice(pages.indexOf(this.currentPage) + ONE_PAGE_UP, SPLICE_AMOUNT);
            const pagesLeftIndex = pagesReversed.indexOf(this.currentPage) + ONE_PAGE_UP;
            this.pagesLeft = pagesReversed.splice(pagesLeftIndex, SPLICE_AMOUNT - this.pagesRight.length).reverse();
        } else if (this.currentPage < PAGE_BUFFER_LEFT) {
            const pagesLeftIndex = pagesReversed.indexOf(this.currentPage) + ONE_PAGE_UP;
            this.pagesLeft = pagesReversed.splice(pagesLeftIndex, SPLICE_AMOUNT).reverse();
            this.pagesRight = pages.splice(this.currentPage, SPLICE_AMOUNT - this.pagesLeft.length);
        } else {
            this.pagesLeft = [this.currentPage + ONE_PAGE_DOWN];
            this.pagesRight = [this.currentPage + ONE_PAGE_UP];
        }
        this.pages = [...this.pagesLeft, this.currentPage, ...this.pagesRight];
        this.ariaCurrent = this.contentService.get('paginator.aria.current') + this.currentPage;
    }

    protected hideWhenOnPage(page: number): string {
        return this.currentPage === page ? 'hidden' : 'visible';
    }

    /**
     * Updates the list of page size options to display to the user. Includes making sure that
     * the page size is an option and that the list is sorted.
     */
    private _updateDisplayedPageSizeOptions(): void {
        if (!this._initialized) {
            return;
        }

        // If no page size is provided, use the first page size option or the default page size.
        if (!this.pageSize) {
            this._pageSize = this.pageSizeOptions.length !== 0 ? this.pageSizeOptions[0] : DEFAULT_PAGE_SIZE;
        }

        this._displayedPageSizeOptions = this.pageSizeOptions.slice();

        if (this._displayedPageSizeOptions.indexOf(this.pageSize) === -1) {
            this._displayedPageSizeOptions.push(this.pageSize);
        }

        // Sort the numbers using a number-specific sort function.
        this._displayedPageSizeOptions.sort((a, b) => a - b);
        this.changeDetectorRef.markForCheck();
    }

    /** Emits an event notifying that a change of the paginator's properties has been triggered. */
    private _emitPageEvent(previousPageIndex: number): void {
        this.handlePageMove();
        this.page.emit({
            previousPageIndex,
            pageIndex: this.pageIndex,
            pageSize: this.pageSize,
            length: this.length
        });
    }
}
